import { type FC, useEffect, useState } from "react";

import { EmbedCardSkeleton } from "~/feature/embed/components/EmbedCard";
import { EmbedMessage } from "~/feature/embed/components/EmbedMessage";
import { EMBED_DOES_NOT_FIT_TITLE } from "~/feature/embed/constants";
import { fitsEmbedSize } from "~/feature/embed/utils/fitsEmbedSize";
import { getEmbedFitMessage } from "~/feature/embed/utils/getEmbedFitMessage";
import { getEmbedMessageSize } from "~/feature/embed/utils/getEmbedMessageSize";
import type { IEmbedSize } from "~/feature/embed/utils/getEmbedMinSize";
import { getEmbedUrl } from "~/feature/embed/utils/getEmbedUrl";
import { toEmbedConfig } from "~/feature/embed/utils/toEmbedConfig";

import { useEmbed } from "../../providers/EmbedProvider";
import { EmbedOptionEnum } from "../../types";
import {
  EMBED_PREVIEW_LOADING_LABEL,
  EMBED_PREVIEW_LOADING_TITLE,
  EMBED_PREVIEW_MAX_HEIGHT,
  EMBED_PREVIEW_SCALE_NOTE_BELOW,
  EMBED_PREVIEW_STALE_MS,
  EMBED_PREVIEW_TITLE,
  getEmbedScaleNote,
} from "./constants";
import * as Styled from "./EmbedPreview.styles";
import { getPreviewFrames } from "./getPreviewFrames";
import { useFitScale } from "./useFitScale";

interface IEmbedPreviewProps {
  size: IEmbedSize;
  minSize: IEmbedSize;
  fitMessageId?: string;
}

// The document a frame has actually loaded — same origin, so it can be asked.
// Anything else (about:blank, an earlier page) is not the one we are waiting for.
const getLoadedHref = (frame: HTMLIFrameElement): string | null => {
  try {
    return frame.contentWindow?.location.href ?? null;
  } catch {
    return null;
  }
};

export const EmbedPreview: FC<IEmbedPreviewProps> = ({
  size,
  minSize,
  fitMessageId,
}) => {
  const { event, markets, selections, options } = useEmbed();
  const [frontSrc, setFrontSrc] = useState<string | null>(null);
  const [staleSrc, setStaleSrc] = useState<string | null>(null);
  const { ref, scale } = useFitScale(size);
  const src = getEmbedUrl(
    toEmbedConfig({ event, markets, selections, options, size: null })
  );
  const frames = getPreviewFrames(frontSrc, src);
  const pendingSrc = frames.find(({ isFront }) => !isFront)?.src ?? null;
  const doesNotFit = !fitsEmbedSize(size, minSize);
  const isStale = pendingSrc !== null && staleSrc === pendingSrc;
  const viewportHeight = doesNotFit
    ? Math.min(getEmbedMessageSize(size).height, EMBED_PREVIEW_MAX_HEIGHT)
    : size.height * scale;

  useEffect(() => {
    if (!pendingSrc) return;

    const timer = window.setTimeout(
      () => setStaleSrc(pendingSrc),
      EMBED_PREVIEW_STALE_MS
    );

    return () => window.clearTimeout(timer);
  }, [pendingSrc]);

  return (
    <Styled.Root ref={ref}>
      <Styled.Viewport $height={viewportHeight}>
        <Styled.Scaled $scale={scale} $width={size.width} $height={size.height}>
          {frames.length > 1 && <Styled.Mask />}

          {frames.map(({ src: frameSrc, isFront }) => (
            <Styled.Frame
              key={frameSrc}
              title={isFront ? EMBED_PREVIEW_TITLE : EMBED_PREVIEW_LOADING_TITLE}
              src={frameSrc}
              width={size.width}
              height={size.height}
              $front={isFront}
              // A covered frame is out of the accessibility tree and out of the
              // tab order alike — nothing behind the mask may take focus.
              aria-hidden={doesNotFit || !isFront}
              inert={doesNotFit || !isFront}
              tabIndex={doesNotFit || !isFront ? -1 : undefined}
              onLoad={({ currentTarget }) => {
                const loaded = getLoadedHref(currentTarget);

                if (loaded === null || loaded === frameSrc) {
                  setFrontSrc(frameSrc);
                }
              }}
            />
          ))}

          {!doesNotFit && (!frontSrc || isStale) && (
            <Styled.Loading
              role="status"
              aria-label={EMBED_PREVIEW_LOADING_LABEL}
            >
              <EmbedCardSkeleton border={options[EmbedOptionEnum.BORDER]} />
            </Styled.Loading>
          )}
        </Styled.Scaled>

        {doesNotFit && (
          <Styled.Fallback id={fitMessageId}>
            <EmbedMessage
              title={EMBED_DOES_NOT_FIT_TITLE}
              detail={getEmbedFitMessage(size, minSize)}
            />
          </Styled.Fallback>
        )}
      </Styled.Viewport>

      {scale < EMBED_PREVIEW_SCALE_NOTE_BELOW && (
        <Styled.ScaleNote>{getEmbedScaleNote(scale)}</Styled.ScaleNote>
      )}
    </Styled.Root>
  );
};
